/*
  Copy-pasted from swagger-ui utils:
  https://github.com/swagger-api/swagger-ui/blob/4d9ec4fb0eaaddb7e1a8f4502e8e8577d40e46ec/src/core/utils/index.js#L711
  https://github.com/swagger-api/swagger-ui/blob/80d56c9518af7ad523f7171815c9da836551e259/src/core/utils/url.js
*/
import Im from 'immutable';
import cssEscape from 'css.escape';

export function isAbsoluteUrl(url) {
  return url.match(/^(?:[a-z]+:)?\/\//i); // Matches http://, HTTP://, https://, ftp://, //example.com,
}

export function addProtocol(url) {
  if (!url.match(/^\/\//i)) return url; // Checks if protocol is missing e.g. //example.com

  return `${window.location.protocol}${url}`;
}

export function buildBaseUrl(selectedServer, specUrl) {
  if (!selectedServer) return specUrl;
  if (isAbsoluteUrl(selectedServer)) return addProtocol(selectedServer);

  return new URL(selectedServer, specUrl).href;
}

export function buildUrl(url, specUrl, { selectedServer = '' } = {}) {
  if (!url) return undefined;
  if (isAbsoluteUrl(url)) return url;

  const baseUrl = buildBaseUrl(selectedServer, specUrl);
  if (!isAbsoluteUrl(baseUrl)) {
    return new URL(url, window.location.href).href;
  }
  return new URL(url, baseUrl).href;
}

/**
 * Safe version of buildUrl function. `selectedServer` can contain server variables
 * which can fail the URL resolution.
 */
export function safeBuildUrl(url, specUrl, { selectedServer = '' } = {}) {
  try {
    return buildUrl(url, specUrl, { selectedServer });
  } catch {
    return undefined;
  }
}

export function sanitizeUrl(url) {
  if (typeof url !== 'string' || url.trim() === '') {
    return '';
  }

  const urlTrimmed = url.trim();
  const blankURL = 'about:blank';

  try {
    const base = `https://base${String(Math.random()).slice(2)}`;
    const urlObject = new URL(urlTrimmed, base);
    const scheme = urlObject.protocol.slice(0, -1);

    // check for invalid schemes
    if (['javascript', 'data', 'vbscript'].includes(scheme.toLowerCase())) {
      return blankURL;
    }

    // return sanitized URI reference
    if (urlObject.origin === base) {
      if (urlTrimmed.startsWith('/')) {
        return `${urlObject.pathname}${urlObject.search}${urlObject.hash}`;
      }
      if (urlTrimmed.startsWith('.')) {
        return `.${urlObject.pathname}${urlObject.search}${urlObject.hash}`;
      }
      return `${urlObject.pathname.substring(1)}${urlObject.search}${urlObject.hash}`;
    }

    return String(urlObject);
  } catch {
    return blankURL;
  }
}

export function isFunc(thing) {
  return typeof thing === 'function';
}

export const createDeepLinkPath = (str) =>
  typeof str === 'string' || str instanceof String ? str.trim().replace(/\s/g, '%20') : '';

// suitable for use in CSS classes and ids
export const escapeDeepLinkPath = (str) => cssEscape(createDeepLinkPath(str).replace(/%20/g, '_'));

const replaceSpecialCharsWithUnderscore = (operationId) => operationId.replace(/\W/gi, '_');

export function idFromPathMethod(pathName, method, { v2OperationIdCompatibilityMode } = {}) {
  if (v2OperationIdCompatibilityMode) {
    let res = `${method.toLowerCase()}_${pathName}`.replace(
      /[\s!@#$%^&*()_+=[{\]};:<>|./?,\\'""-]/g,
      '_',
    );

    res = res || `${pathName.substring(1)}_${method}`;

    return res
      .replace(/((_){2,})/g, '_')
      .replace(/^(_)*/g, '')
      .replace(/([_])*$/g, '');
  }
  return `${method.toLowerCase()}${replaceSpecialCharsWithUnderscore(pathName)}`;
}

// ref: https://github.com/swagger-api/swagger-js/blob/e566fb80d2b5f1d809dfb9db09ec256614c0451d/src/helpers/op-id.js#L5
export default function opId(
  operation,
  pathName,
  method = '',
  { v2OperationIdCompatibilityMode } = {},
) {
  if (!operation || typeof operation !== 'object') {
    return null;
  }
  const idWithoutWhitespace = (operation.operationId || '').replace(/\s/g, '');
  if (idWithoutWhitespace.length) {
    return replaceSpecialCharsWithUnderscore(operation.operationId);
  }
  return idFromPathMethod(pathName, method, { v2OperationIdCompatibilityMode });
}

/**
 * Returns an Immutable List, safely
 * @param {Immutable.Iterable} iterable the iterable to get the key from
 * @param {String|[String]} key either an array of keys, or a single key
 * @returns {Immutable.List} either iterable.get(keys) or an empty Immutable.List
 */
export function getList(iterable, keys) {
  if (!Im.Iterable.isIterable(iterable)) {
    return Im.List();
  }
  const val = iterable.getIn(Array.isArray(keys) ? keys : [keys]);
  return Im.List.isList(val) ? val : Im.List();
}

export const getExtensions = (defObj) => defObj.filter((v, k) => /^x-/.test(k));
